import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import PageHeader from "../../components/PageHeader";
import WorkspacePage from "../../components/WorkspacePage";
import StyledDataGrid from "../../components/StyledDataGrid";
import {
  fetchProviders,
  deleteProvider,
  uploadProvidersCSV,
} from "../../utils/api";
import AddEditProvider from "./AddEditProvider";

const maroon = "#500000";

const ProvidersManagement = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadProviders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchProviders();
      setProviders(data || []);
    } catch (err) {
      console.error("Error loading providers:", err);
      setError("Failed to load providers");
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  // Handle success message from navigation state
  useEffect(() => {
    const state = window.history.state;
    if (state?.usr?.successMessage) {
      setSuccessMessage(state.usr.successMessage);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, []);

  const handleAddProvider = () => {
    setEditingProvider(null);
    setAddEditModalOpen(true);
  };

  const handleEditProvider = (provider) => {
    setEditingProvider(provider);
    setAddEditModalOpen(true);
  };

  const handleDeleteClick = (provider) => {
    setProviderToDelete(provider);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProviderToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!providerToDelete?.providerId) return;

    try {
      setDeleting(true);
      await deleteProvider(providerToDelete.providerId);
      setSuccessMessage("Provider deleted successfully!");
      setDeleteDialogOpen(false);
      setProviderToDelete(null);
      await loadProviders();
    } catch (error) {
      console.error("Error deleting provider:", error);
      setError("Failed to delete provider");
    } finally {
      setDeleting(false);
    }
  };

  const handleModalClose = () => {
    setAddEditModalOpen(false);
    setEditingProvider(null);
  };

  const handleModalSave = () => {
    setSuccessMessage(
      editingProvider
        ? "Provider updated successfully!"
        : "Provider created successfully!"
    );
    handleModalClose();
    loadProviders();
  };

  const handleCSVUploadClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        await handleCSVUpload(file);
      }
    };
    input.click();
  };

  const handleCSVUpload = async (file) => {
    if (!file) return;

    try {
      setUploading(true);
      setError(null);
      const result = await uploadProvidersCSV(file);

      // Format success message based on result
      if (result.success !== undefined && result.total !== undefined) {
        const message = `CSV uploaded successfully! Imported ${
          result.success
        } of ${result.total} providers${
          result.failed > 0 ? ` (${result.failed} failed)` : ""
        }.`;
        setSuccessMessage(message);
      } else {
        setSuccessMessage("CSV uploaded successfully!");
      }

      // Reload providers to show the new data
      await loadProviders();
    } catch (error) {
      console.error("Error uploading CSV:", error);
      setError(
        error.message ||
          "Failed to upload CSV file. Please check the file format and try again."
      );
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => {
        const provider = params.row;
        const fullName =
          provider.name ||
          `${provider.title || ""} ${provider.firstName || ""} ${
            provider.lastName || ""
          } ${provider.suffix || ""}`.trim();
        return fullName || "N/A";
      },
    },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "contactNumber",
      headerName: "Contact",
      flex: 0.5,
      minWidth: 120,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleEditProvider(params.row)}
            sx={{ color: maroon, borderRadius: "10px" }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: "#d32f2f", borderRadius: "10px" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <WorkspacePage>
      <PageHeader
        title="Program Directory"
        actions={
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadProviders}
              disabled={loading || uploading}
              sx={{
                textTransform: "none",
                borderColor: maroon,
                color: maroon,
                "&:hover": {
                  borderColor: maroon,
                  backgroundColor: "rgba(80, 0, 0, 0.1)",
                },
              }}
            >
              REFRESH
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={handleCSVUploadClick}
              disabled={loading || uploading}
              sx={{
                textTransform: "none",
                borderColor: maroon,
                color: maroon,
                "&:hover": {
                  borderColor: maroon,
                  backgroundColor: "rgba(80, 0, 0, 0.1)",
                },
              }}
            >
              {uploading ? "UPLOADING..." : "UPLOAD CSV"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddProvider}
              disabled={uploading}
              sx={{
                backgroundColor: maroon,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#4a0000",
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                },
              }}
            >
              ADD PROVIDER
            </Button>
          </Box>
        }
      />

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage("")}
          sx={{ mb: 2 }}
        >
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Providers Table */}
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", mt: 2 }}>
        <StyledDataGrid
          rows={providers.map((provider, index) => ({
            ...provider,
            id:
              provider.providerId ||
              provider.id ||
              `provider-${index}-${provider.lastName}-${provider.firstName}`,
          }))}
          columns={columns}
          pageSizeOptions={[10, 25, 50, 100]}
          pagination
          disableSelectionOnClick
          loading={loading}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          getRowId={(row) => row.id}
          getRowHeight={() => 'auto'}
          sx={{
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: 1.5,
              py: 1,
            }
          }}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Provider</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this provider? This action cannot be
            undone.
          </DialogContentText>
          {providerToDelete && (
            <DialogContentText sx={{ mt: 1, fontWeight: 600 }}>
              Provider:{" "}
              {providerToDelete.name ||
                `${providerToDelete.firstName} ${providerToDelete.lastName}`}{" "}
              ({providerToDelete.providerId})
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleting}
            sx={{
              textTransform: "none",
              color: maroon,
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {deleting ? "DELETING..." : "DELETE"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Provider Modal */}
      <AddEditProvider
        open={addEditModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        provider={editingProvider}
      />
    </WorkspacePage>
  );
};

export default ProvidersManagement;
